<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div class="mb-12">
      <h1 class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900">
        My Companies
      </h1>
    </div>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <div v-if="loading" class="text-gray-500">Loading companies...</div>
      <div v-else-if="error" class="text-red-500">{{ error }}</div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-16">
        <div
          v-for="c in companies"
          :key="c.id"
          class="company-card bg-white rounded-2xl shadow border border-gray-200 overflow-hidden relative"
        >
          <div class="card-img bg-gray-50 grid place-items-center border-b border-gray-200">
            <img v-if="logoUrl(c)" :src="logoUrl(c)" alt="logo" class="max-h-full w-auto object-contain" />
            <div v-else class="text-gray-400 text-sm">No Logo</div>
          </div>

          <div class="card-body p-5">
            <div class="text-lg font-bold text-gray-900 truncate" :title="c.name">{{ c.name }}</div>
            <div class="text-sm text-gray-700 mt-1">{{ c.location || '-' }}</div>
          </div>

          <div class="dim-overlay"></div>
          <div class="edit-overlay">
            <button
              class="edit-btn inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold text-white shadow-sm"
              :style="{ backgroundColor: '#5B9AC5' }"
              @click="openView(c)"
            >
              <svg viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5ZM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5Zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3Z" fill="currentColor"/>
              </svg>
              View
            </button>
          </div>
        </div>
      </div>

      <!-- View Modal -->
      <transition name="fade">
        <div
          v-if="showModal"
          class="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-4"
          @click.self="closeModal"
        >
          <div class="bg-white w-[92%] max-w-lg rounded-2xl shadow p-6 relative">
            <button
              class="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
              @click="closeModal"
              aria-label="Close"
            >✕</button>

            <h3 class="text-[20px] font-bold text-center mb-6">Company Details</h3>

            <div class="flex justify-center mb-6">
              <div class="w-32 h-32 rounded-xl border border-gray-200 bg-gray-50 grid place-items-center overflow-hidden">
                <img v-if="logoUrl(selected)" :src="logoUrl(selected)" alt="logo" class="max-h-full w-auto object-contain" />
                <div v-else class="text-gray-400 text-sm">No Logo</div>
              </div>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Company Name</label>
                <div class="w-full h-10 rounded-xl border-[2px] border-gray-200 px-3 text-sm bg-gray-50 flex items-center text-gray-900">
                  {{ selected.name }}
                </div>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Location</label>
                <div class="w-full h-10 rounded-xl border-[2px] border-gray-200 px-3 text-sm bg-gray-50 flex items-center text-gray-900">
                  {{ selected.location || '-' }}
                </div>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Tax Reg. No</label>
                <div class="w-full h-10 rounded-xl border-[2px] border-gray-200 px-3 text-sm bg-gray-50 flex items-center text-gray-900">
                  {{ selected.tax_number || '-' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import api from "@/api";
import { loadMyPermissions, hasAny } from "@/utils/permissions";

export default {
  name: "CompaniesList",

  data() {
    return {
      can: {
        view: false, // others.my_companies
      },
      companies: [],
      loading: true,
      error: null,
      showModal: false,
      selected: null,
    };
  },

  async mounted() {
    await this.ensurePermissions();
    if (!this.can.view) {
      this.loading = false;
      return;
    }
    this.fetchCompanies();
  },

  methods: {
    async ensurePermissions() {
      try {
        await loadMyPermissions(false);
        this.can.view = hasAny(["others.my_companies"]);
      } catch (_) {
        this.can.view = false;
      }
    },

    async fetchCompanies() {
      try {
        const res = await api.get("/accounting/companies");
        this.companies = res.data?.data ?? res.data;
      } catch {
        this.error = "Failed to load companies.";
      } finally {
        this.loading = false;
      }
    },

    openView(company) {
      this.selected  = company;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selected  = null;
    },

    logoUrl(c) {
      if (!c) return "";

      let raw = c.logo || c.logo_url;
      if (!raw) return "";

      raw = raw.replace(
        "https://accounting.lyon-jo.com",
        "https://booking.lyon-jo.com"
      );

      return raw;
    },
  },
};
</script>

<style scoped>
.company-card .card-img { height: 225px; }
.company-card .card-body { height: 116px; }
.company-card .dim-overlay {
  position: absolute; inset: 0;
  background: rgba(0,0,0,.55);
  opacity: 0; transition: opacity .18s ease;
  z-index: 5; pointer-events: none;
}
.company-card .edit-overlay {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity .18s ease, transform .18s ease;
  z-index: 10; pointer-events: none;
}
.company-card .edit-btn {
  transform: translateY(4px) scale(.98);
  pointer-events: auto;
}
.company-card:hover .dim-overlay  { opacity: 1; }
.company-card:hover .edit-overlay { opacity: 1; }
.company-card:hover .edit-btn     { transform: translateY(0) scale(1); }
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
